import { getFeatureFlagValue } from '@citrite/feature-flags';
import {
	billingPiletExtensionSlots,
	billingPiletModals,
	PaymentGatewayProps,
} from '@sharefiledev/billing-pilet-sdk';
import type { PiletApi, PiletUserContext } from '@sharefiledev/sharefile-appshell';
import { ApplicationContextProvider } from './ApplicationContextProvider';
import { AsyncCreditCardModal } from './billing/components';
import { AsyncEditBillingInfo } from './billing/EditBilling';
import {
	AsyncCreditCardOverdueInvoicesPage,
	AsyncCreditCardOverduePage,
	AsyncCreditCardOverduePayStatusPage,
	AsyncInvoiceOverdueInvoicesPage,
	AsyncInvoiceOverduePage,
	AsyncUpdateCreditCardPage,
} from './billing/Overdue';
import { AsyncReceiptsPage } from './billing/Receipts';
import { FeatureFlag } from './featureFlags';
import {
	isExpiredTrial,
	showCancelAccount,
	showConvertToPaid,
	showCreditCardOverduePage,
	showEditBillingInfo,
	showExpirationPurchasePage,
	showInvoiceOverduePage,
	showManagePlan,
	showReactivateAccount,
	showReceipts,
	showTrialBanner,
} from './permissions';
import { AsyncCancelAccount } from './provisioning/CancelAccount';
import { AsyncConvertToPaid } from './provisioning/ConvertToPaid';
import { AsyncExpirationPage } from './provisioning/Expiration';
import { AsyncExpirationPurchasePage } from './provisioning/Expiration/Purchase';
import { AsyncManagePlan } from './provisioning/ManagePlan';
import { AsyncReactivatePage } from './provisioning/ReactivateAccount';
import { AsyncTrialInformationBanner } from './provisioning/TrialInformationBanner';
import { RenderConditionally } from './RenderConditionally';
import {
	AdminBillingRoute,
	CancelAccountRoute,
	ConvertFreeTrialRoute,
	CreditCardOverdueInvoicesRoute,
	CreditCardOverduePayRoute,
	CreditCardOverduePayStatusRoute,
	CreditCardOverdueRoute,
	EditBillingInfoRoute,
	ExpirationPurchaseRoute,
	ExpirationRoute,
	InvoiceOverdueInvoicesRoute,
	InvoiceOverdueRoute,
	ManagePlanRoute,
	ReactivateRoute,
	ReceiptsRoute,
} from './routes';
import { t } from './translate';
import { getDefaultModalProps } from './utils';

export function setup(piletApi: PiletApi) {
	const context: PiletUserContext = piletApi.sf.piletUserContext.get();
	const account = context.account;
	const accountId = context.account.Id;
	const userId = context.user.Id;
	const isFINRA = context.account.Preferences?.IsFINRA ?? false;
	const isVDR = (context.account.PlanTrack ?? '') === 'VDR';
	const isPlannedBillingDowntimeEnabled = getFeatureFlagValue(
		FeatureFlag.PlannedBillingDowntime,
		false
	);
	const usePiletPageForCancelAccount = getFeatureFlagValue(
		FeatureFlag.UsePiletPageForCancelAccount,
		false
	);

	if (showConvertToPaid(context) || showEditBillingInfo(context)) {
		piletApi.registerModal<PaymentGatewayProps>(
			billingPiletModals.creditCardModal,
			({ completeInteraction, cancelInteraction, updateModalProps, options }) => (
				<AsyncCreditCardModal
					completeInteraction={completeInteraction}
					cancelInteraction={cancelInteraction}
					updateModalProps={updateModalProps}
					{...options!}
				/>
			),
			{
				modalProps: {
					title: getFeatureFlagValue(FeatureFlag.EnablePaymentGatewayV2)
						? t('payment_gateway.modal_title')
						: t('payment_gateway.modal_title_legacy'),
					...getDefaultModalProps(),
				},
			}
		);
	}

	if (accountId && userId && showManagePlan(context) && showTrialBanner(context)) {
		piletApi.registerExtension(billingPiletExtensionSlots.trialBanner, ({ params }) => (
			<AsyncTrialInformationBanner {...params} />
		));
	}

	if (!isPlannedBillingDowntimeEnabled) {
		if (accountId && userId && showManagePlan(context) && !showConvertToPaid(context)) {
			piletApi.registerPage(ManagePlanRoute, () => (
				<ApplicationContextProvider piletApi={piletApi}>
					<AsyncManagePlan
						datePreferences={context.user.Preferences?.DateFormat}
						timePreferences={context.user.Preferences?.TimeFormat}
						isVDR={isVDR}
						isFINRA={isFINRA}
					/>
				</ApplicationContextProvider>
			));

			piletApi.sf.registerLeftSecondaryNavChildComponent({
				href: ManagePlanRoute,
				title: () => t('manage_plan.left_nav_button_text') ?? '...',
				weight: 200,
				parent: AdminBillingRoute,
			});
		}

		if (showCancelAccount(context)) {
			if (usePiletPageForCancelAccount) {
				piletApi.registerPage(CancelAccountRoute, () => (
					<ApplicationContextProvider piletApi={piletApi}>
						<AsyncCancelAccount account={account} />
					</ApplicationContextProvider>
				));
				piletApi.sf.registerLeftSecondaryNavChildComponent({
					href: CancelAccountRoute,
					title: () =>
						account.IsFreeTrial
							? t('cancel_account.cancel_trial')
							: t('cancel_account.cancel_account'),
					weight: 1000, // Ensure it is last
					parent: AdminBillingRoute,
				});
			} else {
				piletApi.registerExtension(billingPiletExtensionSlots.cancelAccount, () => {
					return (
						<ApplicationContextProvider piletApi={piletApi}>
							<AsyncCancelAccount account={account} />
						</ApplicationContextProvider>
					);
				});
			}
		}

		if (showConvertToPaid(context)) {
			piletApi.registerPage(`${ConvertFreeTrialRoute}/:orderId?/:successFlag?`, () => (
				<ApplicationContextProvider piletApi={piletApi}>
					<AsyncConvertToPaid />
				</ApplicationContextProvider>
			));

			piletApi.sf.registerLeftSecondaryNavChildComponent({
				href: ConvertFreeTrialRoute,
				title: () => t('convert_to_paid.left_nav_button_text') ?? '...',
				weight: 200,
				parent: AdminBillingRoute,
			});
		}

		if (showReactivateAccount(context)) {
			piletApi.sf.registerLeftSecondaryNavChildComponent({
				href: ReactivateRoute,
				title: () => t('reactivate.left_nav_button_text') ?? '...',
				weight: 200,
				parent: AdminBillingRoute,
			});
			piletApi.registerPage(ReactivateRoute, () => (
				<ApplicationContextProvider piletApi={piletApi}>
					<AsyncReactivatePage />
				</ApplicationContextProvider>
			));
		}

		if (showExpirationPurchasePage(context)) {
			piletApi.registerPage(
				`${ExpirationPurchaseRoute}/:orderId?/:successFlag?`,
				() => (
					<ApplicationContextProvider piletApi={piletApi}>
						<AsyncExpirationPurchasePage />
					</ApplicationContextProvider>
				),
				{
					pageType: 'blankPage',
				}
			);
		} else if (!userId) {
			// If the user is not logged in, we still want to register the page for the expiration purchase route
			// so that WebApp can redirect the user to the login page when they try to access it
			// and redirect back to this route after login.
			piletApi.registerPage(
				`${ExpirationPurchaseRoute}/:orderId?/:successFlag?`,
				() => null
			);
		}

		piletApi.registerPage(
			ExpirationRoute,
			() => (
				<RenderConditionally redirectToDashboard={!isExpiredTrial(context)}>
					<ApplicationContextProvider piletApi={piletApi}>
						<AsyncExpirationPage />
					</ApplicationContextProvider>
				</RenderConditionally>
			),
			{
				pageType: 'blankPage',
			}
		);

		if (accountId && userId && showEditBillingInfo(context)) {
			piletApi.registerPage(`${EditBillingInfoRoute}/:orderId?/:successFlag?`, () => (
				<ApplicationContextProvider piletApi={piletApi}>
					<AsyncEditBillingInfo />
				</ApplicationContextProvider>
			));

			piletApi.sf.registerLeftSecondaryNavChildComponent({
				href: EditBillingInfoRoute,
				title: () => t('edit_billing_information.left_nav_button_text') ?? '...',
				weight: 200,
				parent: AdminBillingRoute,
			});
		}

		if (showReceipts(context)) {
			piletApi.sf.registerLeftSecondaryNavChildComponent({
				href: ReceiptsRoute,
				title: () => t('receipts.left_nav_button_text') ?? '...',
				weight: 200,
				parent: AdminBillingRoute,
			});
			piletApi.registerPage(`${ReceiptsRoute}/:orderId?/:successFlag?`, () => (
				<ApplicationContextProvider piletApi={piletApi}>
					<AsyncReceiptsPage />
				</ApplicationContextProvider>
			));
		}

		if (showInvoiceOverduePage(context)) {
			if (showReceipts(context)) {
				piletApi.registerPage(
					InvoiceOverdueInvoicesRoute,
					() => (
						<ApplicationContextProvider piletApi={piletApi}>
							<AsyncInvoiceOverdueInvoicesPage />
						</ApplicationContextProvider>
					),
					{
						pageType: 'blankPage',
					}
				);
			}

			piletApi.registerPage(
				InvoiceOverdueRoute,
				() => (
					<ApplicationContextProvider piletApi={piletApi}>
						<AsyncInvoiceOverduePage />
					</ApplicationContextProvider>
				),
				{
					pageType: 'blankPage',
				}
			);
		}

		if (showCreditCardOverduePage(context)) {
			piletApi.registerPage(
				`${CreditCardOverduePayStatusRoute}/:orderId?/:successFlag?`,
				() => (
					<ApplicationContextProvider piletApi={piletApi}>
						<AsyncCreditCardOverduePayStatusPage />
					</ApplicationContextProvider>
				),
				{
					pageType: 'blankPage',
				}
			);

			piletApi.registerPage(
				CreditCardOverduePayRoute,
				() => (
					<ApplicationContextProvider piletApi={piletApi}>
						<AsyncUpdateCreditCardPage />
					</ApplicationContextProvider>
				),
				{
					pageType: 'blankPage',
				}
			);

			if (showReceipts(context)) {
				piletApi.registerPage(
					CreditCardOverdueInvoicesRoute,
					() => (
						<ApplicationContextProvider piletApi={piletApi}>
							<AsyncCreditCardOverdueInvoicesPage />
						</ApplicationContextProvider>
					),
					{
						pageType: 'blankPage',
					}
				);
			}

			piletApi.registerPage(
				CreditCardOverdueRoute,
				() => (
					<ApplicationContextProvider piletApi={piletApi}>
						<AsyncCreditCardOverduePage />
					</ApplicationContextProvider>
				),
				{
					pageType: 'blankPage',
				}
			);
		}
	}
}
